// @repletoryreach/sdk-utils/whatsapp-text: WhatsApp's own formatting, parsed.
//
// WhatsApp is NOT Markdown and must not be parsed with a Markdown library. It
// has four markers, `*bold*`, `_italic_`, `~strikethrough~` and `` `monospace` ``
// (three backticks for a block), and one rule that a Markdown parser gets
// wrong: a marker only counts when it wraps text and is not part of a word. So
// `2*3*4` is the number it looks like, a lone `*` is an asterisk, and the stray
// underscores that fill real message bodies stay underscores.
//
// The order of the passes matters more than the rules do:
//
//   1. links become atoms          (links.ts explains why this is first)
//   2. code spans become atoms     (nothing inside monospace is formatted)
//   3. the emphasis pass runs over what is left
//
// Nothing here produces HTML. The result is a small tree that render.tsx turns
// into React elements, so a message body from a stranger can never inject
// markup, and there is no `dangerouslySetInnerHTML` to get wrong.

import { findLinks, type LinkKind } from './links.js';

export type WaNode =
  | { type: 'text'; value: string }
  | { type: 'bold'; children: WaNode[] }
  | { type: 'italic'; children: WaNode[] }
  | { type: 'strike'; children: WaNode[] }
  /** Single backticks: monospace within a line. */
  | { type: 'code'; value: string }
  /** Triple backticks: monospace on its own, never linkified. */
  | { type: 'codeblock'; value: string }
  | { type: 'link'; kind: LinkKind; href: string; text: string; title: string };

export interface ParseOptions {
  /** Turn URLs, e-mail addresses and international numbers into links. Default true. */
  links?: boolean;
  /** Longest visible link label before the middle of a URL is dropped. Default 48. */
  maxLinkChars?: number;
}

/** A marker character and the node it opens. */
const MARKERS: Record<string, 'bold' | 'italic' | 'strike'> = {
  '*': 'bold',
  _: 'italic',
  '~': 'strike'
};

/**
 * One position in the body: either a single character, or something already
 * decided (a link, a code span) that the emphasis pass treats as one opaque
 * word and can never reach inside.
 */
type Unit =
  | { k: 'ch'; c: string }
  | { k: 'atom'; node: WaNode };

/**
 * Line endings normalised, trailing spaces on a line dropped, trailing blank
 * lines at the end dropped. Nothing else is collapsed: a message body's own
 * spacing is the sender's, including the blank line between paragraphs and any
 * leading indent.
 */
function normalise(input: string): string {
  return input.replace(/\r\n?/g, '\n').replace(/[ \t]+(?=\n)/g, '').replace(/\s+$/, '');
}

const isSpace = (u: Unit | undefined) => !!u && u.k === 'ch' && /\s/.test(u.c);
/** An atom counts as part of a word, so `example.com*bold*` does not open a span. */
const isWord = (u: Unit | undefined) => !!u && (u.k === 'atom' || /[\p{L}\p{N}]/u.test(u.c));
const charAt = (u: Unit | undefined) => (u && u.k === 'ch' ? u.c : '');

/**
 * Splits the body into units: links first, then code spans, then plain
 * characters. Each atom is indivisible from here on.
 */
function tokenise(text: string, opts: ParseOptions): Unit[] {
  const links = opts.links === false ? [] : findLinks(text, opts.maxLinkChars ?? 48);
  const byIndex = new Map(links.map((l) => [l.index, l]));
  const units: Unit[] = [];

  for (let i = 0; i < text.length; ) {
    const link = byIndex.get(i);
    if (link) {
      units.push({ k: 'atom', node: { type: 'link', kind: link.kind, href: link.href, text: link.text, title: link.title } });
      i += link.raw.length;
      continue;
    }

    if (text.startsWith('```', i)) {
      const end = text.indexOf('```', i + 3);
      if (end > i + 3) {
        // A leading newline after the fence is the fence's, not the code's.
        const value = text.slice(i + 3, end).replace(/^\n/, '').replace(/\n$/, '');
        units.push({ k: 'atom', node: { type: 'codeblock', value } });
        i = end + 3;
        continue;
      }
    }

    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i + 1) {
        units.push({ k: 'atom', node: { type: 'code', value: text.slice(i + 1, end) } });
        i = end + 1;
        continue;
      }
    }

    units.push({ k: 'ch', c: text[i] });
    i += 1;
  }
  return units;
}

/**
 * A span may not run over a blank line. Without this one stray asterisk in a
 * long message pairs with another twenty lines below it and puts half the
 * message in bold, which is worse than showing the asterisk.
 */
function blankLineAt(units: Unit[], i: number): boolean {
  if (charAt(units[i]) !== '\n') return false;
  for (let j = i + 1; j < units.length; j += 1) {
    const c = charAt(units[j]);
    if (c === '\n') return true;
    if (c === ' ' || c === '\t') continue;
    return false;
  }
  return false;
}

/**
 * `*` at `i` opens a span when it is not part of a word and the thing after it
 * is text rather than space. This is the rule that keeps `2*3*4` literal: the
 * first `*` sits against a digit.
 */
function opens(units: Unit[], i: number, marker: string): boolean {
  if (isWord(units[i - 1])) return false;
  const next = units[i + 1];
  return !!next && !isSpace(next) && charAt(next) !== marker;
}

/** The mirror rule: text before it, and no word carrying straight on after it. */
function closes(units: Unit[], j: number): boolean {
  return !isSpace(units[j - 1]) && !isWord(units[j + 1]);
}

/** The first valid closer for the marker opened at `from`, or -1. */
function findClose(units: Unit[], from: number, marker: string, end: number): number {
  for (let j = from + 2; j < end; j += 1) {
    if (blankLineAt(units, j)) return -1;
    const u = units[j];
    if (u.k !== 'ch' || u.c !== marker) continue;
    if (closes(units, j)) return j;
  }
  return -1;
}

/** Runs of plain characters become one text node each; atoms pass straight through. */
function emphasis(units: Unit[], start: number, end: number): WaNode[] {
  const out: WaNode[] = [];
  let buffer = '';
  const flush = () => { if (buffer) { out.push({ type: 'text', value: buffer }); buffer = ''; } };

  for (let i = start; i < end; ) {
    const u = units[i];
    if (u.k === 'atom') { flush(); out.push(u.node); i += 1; continue; }

    const type = MARKERS[u.c];
    if (type && opens(units, i, u.c)) {
      const close = findClose(units, i, u.c, end);
      if (close > 0) {
        flush();
        out.push({ type, children: emphasis(units, i + 1, close) });
        i = close + 1;
        continue;
      }
    }

    buffer += u.c;
    i += 1;
  }
  flush();
  return out;
}

/**
 * A message body as a tree of nodes. Safe on null, safe on rubbish: anything
 * that is not valid formatting comes back as the characters the sender typed.
 */
export function parseWhatsAppText(input: string | null | undefined, opts: ParseOptions = {}): WaNode[] {
  const text = normalise(input ?? '');
  if (!text) return [];
  const units = tokenise(text, opts);
  return emphasis(units, 0, units.length);
}
